import React from 'react';

import {
  Alert,
  Box,
  Button,
  Center,
  createStyles,
  Drawer,
  Modal,
  Skeleton,
  Text,
  Title,
  useMantineTheme,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconExclamationCircle } from '@tabler/icons-react';
import BackButton from 'components/BackButton';
import { useAuth } from 'context/auth-context';
import useAthlete from 'hooks/athlete/useAthlete';
import useResendActivation from 'hooks/users/useSendActivationEmail';
import DrawerAthleteForm from 'pages/private/owner//Athletes/components/DrawerAthleteForm';
import AthleteActionsMenu from 'pages/private/owner/AthleteDetail/components/AthleteActionsMenu';
import DeleteAthleteModal from 'pages/private/owner/Athletes/components/DeleteAthleteModal';
import UpdateAthleteNotificationsSettings from 'pages/private/owner/Athletes/components/UpdateAthleteNotificationsSettingModal';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { AthleteRetrieve } from 'types';
import notificationHandler from 'utils/notifications';

import Attendance from './components/Attendance';
import Communications from './components/Communications';
import Documents from './components/Documents';
import GeneralInfo from './components/GeneralInfo';
import Invoice from './components/Invoice';
import Notes from './components/Notes';
import Packages from './components/Packages';
import Reservations from './components/Reservations';

const useStyles = createStyles(theme => ({
  drawer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xs,
    marginLeft: theme.spacing.xs,
  },
  link: {
    textDecoration: 'none',
  },
}));

const AthleteDetail = (): JSX.Element => {
  const { classes } = useStyles();
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const currentLocation = location.pathname.split('/').pop();
  const { athleteId } = useParams<{ athleteId: string }>();

  const {
    data: athleteData,
    isLoading: athleteIsLoading,
    isError: athleteIsError,
    isSuccess: athleteIsSuccess,
  } = useAthlete(athleteId);

  const [isTabsDrawerOpen, { open: openTabsDrawer, close: closeTabsDrawer }] = useDisclosure(false);
  const [isEditFormOpened, { open: openEditForm, close: closeEditForm }] = useDisclosure(false);
  const [
    isCreateLinkedProfileFormOpened,
    { open: openCreateLinkedProfileForm, close: closeCreateLinkedProfileForm },
  ] = useDisclosure(false);
  const [
    isCreateDependentProfileFormOpened,
    { open: openCreateDependentProfileForm, close: closeCreateDependentProfileForm },
  ] = useDisclosure(false);
  const [isDeleteModalOpen, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);
  const [isNotificationsModalOpen, { open: openNotificationsModal, close: closeNotificationsModal }] =
    useDisclosure(false);

  const { successNotification, errorNotification } = notificationHandler();

  const { session, hasPerm } = useAuth();

  const athletesPerm = hasPerm('AthleteMutate');
  const eventMutatePerm = hasPerm('EventMutate');
  const athletePackageMutatePerm = hasPerm('AthletePackageMutate');
  const invoiceAthleteViewPerm = hasPerm('InvoiceAthleteView');
  const issuedDocumentMutatePerm = hasPerm('IssuedDocumentMutate');

  const getAthleteTabs = (
    athlete: AthleteRetrieve,
  ): { label: string; value: string; component: JSX.Element; disabled?: boolean }[] => [
    {
      label: 'General Info',
      value: 'general-info',
      component: <GeneralInfo athlete={athlete} openTabsDrawer={openTabsDrawer} permission={athletesPerm} />,
    },
    {
      label: 'Packages',
      value: 'packages',
      component: (
        <Packages athlete={athlete} openTabsDrawer={openTabsDrawer} permission={athletePackageMutatePerm} />
      ),
    },
    {
      label: 'Documents',
      value: 'documents',
      component: (
        <Documents athlete={athlete} permission={issuedDocumentMutatePerm} openTabsDrawer={openTabsDrawer} />
      ),
    },
    {
      label: 'Invoices',
      value: 'invoices',
      component: <Invoice athlete={athlete} openTabsDrawer={openTabsDrawer} />,
      disabled: !invoiceAthleteViewPerm,
    },
    {
      label: 'Reservations',
      value: 'reservations',
      component: (
        <Reservations athlete={athlete} permission={eventMutatePerm} openTabsDrawer={openTabsDrawer} />
      ),
    },
    {
      label: 'Attendance',
      value: 'attendance',
      component: (
        <Attendance athlete={athlete} permission={eventMutatePerm} openTabsDrawer={openTabsDrawer} />
      ),
    },
    {
      label: 'Communications',
      value: 'communications',
      component: <Communications athlete={athlete} openTabsDrawer={openTabsDrawer} />,
    },
    {
      label: 'Notes',
      value: 'notes',
      component: <Notes athlete={athlete} permission={athletesPerm} openTabsDrawer={openTabsDrawer} />,
    },
  ];

  const resendActivationMutation = useResendActivation();

  const handleSendLoginSetupEmail = (athlete: AthleteRetrieve): void => {
    resendActivationMutation.mutate(athlete.user, {
      onSuccess: () => {
        successNotification('Login setup email successfully sent');
      },
      onError: () => {
        errorNotification('Login setup email failed to send');
      },
    });
  };

  const handleOnClick = (): void => {
    navigate(-1);
  };

  if (athleteIsError) {
    return (
      <Center sx={{ flexGrow: 1 }} p='lg'>
        <Alert icon={<IconExclamationCircle size={theme.fontSizes.md} />} title='Error' color='red'>
          Something went wrong while fetching data!
        </Alert>
      </Center>
    );
  }

  const renderTab = (): JSX.Element | undefined => {
    if (!athleteIsSuccess) return;
    return getAthleteTabs(athleteData.data).find(item => item.value === currentLocation)?.component;
  };

  return (
    <>
      <div className={'header'}>
        <BackButton handleOnClick={handleOnClick} />
        {athleteIsLoading && <Skeleton height='2.8rem' sx={{ width: '10rem' }} />}
        {athleteIsSuccess && (
          <Title order={4} truncate>
            {athleteData.data.fullName}
          </Title>
        )}
        {athleteIsSuccess && (
          <AthleteActionsMenu
            athleteRetrieve={athleteData.data}
            handleEdit={openEditForm}
            handleDelete={openDeleteModal}
            handleSendLoginSetupEmail={handleSendLoginSetupEmail}
            handleCreateLinkedProfile={openCreateLinkedProfileForm}
            handleCreateDependentProfile={openCreateDependentProfileForm}
            handleNotificationsSettings={openNotificationsModal}
            target={
              <Button size='xs' disabled={!athletesPerm}>
                Actions
              </Button>
            }
            disabled={!athletesPerm}
            wodupEnabled={!!session?.gym?.wodupEnabled}
          />
        )}
      </div>

      <div className='content'>{renderTab()}</div>

      <DrawerAthleteForm
        type={
          isEditFormOpened
            ? 'edit'
            : isCreateLinkedProfileFormOpened
            ? 'create-linked'
            : isCreateDependentProfileFormOpened
            ? 'create-dependent'
            : 'closed'
        }
        opened={isEditFormOpened || isCreateLinkedProfileFormOpened || isCreateDependentProfileFormOpened}
        closeHandler={
          isEditFormOpened
            ? closeEditForm
            : isCreateLinkedProfileFormOpened
            ? closeCreateLinkedProfileForm
            : closeCreateDependentProfileForm
        }
        athleteId={isEditFormOpened ? athleteId : undefined}
        primaryAthleteId={isEditFormOpened ? undefined : athleteId}
      />

      <Modal
        opened={isDeleteModalOpen}
        onClose={closeDeleteModal}
        title={<Title order={4}>Delete Athlete</Title>}
        trapFocus
        centered
        size='auto'
      >
        <DeleteAthleteModal athleteId={athleteId} handleClose={closeDeleteModal} redirect />
      </Modal>

      <Modal
        opened={isNotificationsModalOpen}
        onClose={closeNotificationsModal}
        title={<Title order={4}>Notifications Settings</Title>}
        trapFocus
        centered
        size='auto'
        sx={{
          '.mantine-Modal-header': {
            borderBottom: `1px solid ${theme.colors.gray[3]}`,
          },
        }}
      >
        <UpdateAthleteNotificationsSettings athleteId={athleteId} handleClose={closeNotificationsModal} />
      </Modal>

      <Drawer position='bottom' opened={isTabsDrawerOpen} onClose={closeTabsDrawer}>
        <Box className={classes.drawer}>
          {athleteData &&
            getAthleteTabs(athleteData.data).map((item, index) => (
              <Link
                onClick={closeTabsDrawer}
                to={`/users/athletes/${athleteId}/${item.value}`}
                key={item.value + index}
                className={classes.link}
              >
                <Text size='md' fw='bold' color={item.value === currentLocation ? 'kiloBlue' : 'black'}>
                  {item.label}
                </Text>
              </Link>
            ))}
        </Box>
      </Drawer>
    </>
  );
};

export default AthleteDetail;
